/**
 * FeatureTracker — sparse optical flow: detect corners, track them frame to
 * frame, and report (a) a robust global translation (median of point flow) for
 * VO, and (b) the tracked points themselves for the 3D-scan triangulation
 * overlay. This is the "sparse features" upgrade: better motion than global
 * block-matching, and it produces the point cloud the scan visual draws.
 *
 * Corner detection is a lightweight Harris response with non-max suppression;
 * tracking is a small local block-match per point. All on a downscaled greyscale
 * frame, so it stays cheap enough for ~12–15 Hz on a mid phone.
 */

import type { FrameProvider } from './FrameProvider.js';

export interface TrackedPoint {
  x: number; // normalised 0..1 image space
  y: number;
  age: number; // frames survived
  /**
   * Stable identity for as long as this point survives.
   *
   * The tracker always kept identity internally — a point carries forward through
   * optical flow — but export discarded it, leaving callers with an anonymous
   * cloud. Depth-based scale needs to say "this pixel now is that pixel then" to
   * triangulate, so the id is now carried through. Ids are never reused.
   */
  id: number;
}

export interface TrackResult {
  /** normalised global translation since last frame, -1..1 of frame size. */
  dx: number;
  dy: number;
  confidence: number; // 0..1
  points: TrackedPoint[];
}

const W = 80;
const H = 60;
const MAX_POINTS = 48;
const MIN_POINTS = 16;
const SEARCH = 3;
const HARRIS_K = 0.05;

interface Pt {
  x: number;
  y: number;
  age: number;
  id: number;
}

function isFrameProvider(s: FrameProvider | CanvasImageSource): s is FrameProvider {
  return typeof (s as FrameProvider).drawTo === 'function';
}

export class FeatureTracker {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private prev: Float32Array | null = null;
  private curr = new Float32Array(W * H);
  private points: Pt[] = [];
  /** Monotonic id source; never reused, so a cross-frame match is unambiguous. */
  private nextId = 1;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = W;
    this.canvas.height = H;
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;
  }

  reset(): void {
    this.prev = null;
    this.points = [];
  }

  private grab(source: FrameProvider | CanvasImageSource): boolean {
    if (isFrameProvider(source)) {
      if (!source.drawTo(this.ctx, W, H)) return false;
    } else {
      this.ctx.drawImage(source, 0, 0, W, H);
    }
    const img = this.ctx.getImageData(0, 0, W, H).data;
    for (let i = 0, p = 0; i < img.length; i += 4, p++) {
      this.curr[p] = (img[i]! * 0.299 + img[i + 1]! * 0.587 + img[i + 2]! * 0.114) / 255;
    }
    return true;
  }

  /** Harris corners with non-max suppression, spaced out, strongest first. */
  private detect(): void {
    const g = this.curr;
    const resp = new Float32Array(W * H);
    for (let y = 1; y < H - 1; y++) {
      for (let x = 1; x < W - 1; x++) {
        const i = y * W + x;
        const ix = g[i + 1]! - g[i - 1]!;
        const iy = g[i + W]! - g[i - W]!;
        // structure tensor over the 3x3 window (approx via center gradients)
        let a = 0;
        let b = 0;
        let c = 0;
        for (let oy = -1; oy <= 1; oy++) {
          for (let ox = -1; ox <= 1; ox++) {
            const j = (y + oy) * W + (x + ox);
            const jx = g[j + 1]! - g[j - 1]!;
            const jy = g[j + W]! - g[j - W]!;
            a += jx * jx;
            b += jx * jy;
            c += jy * jy;
          }
        }
        const det = a * c - b * b;
        const trace = a + c;
        resp[i] = det - HARRIS_K * trace * trace + ix * 0 + iy * 0;
      }
    }
    // Non-max suppression with a spacing radius; collect strongest.
    const radius = 4;
    const found: { x: number; y: number; r: number }[] = [];
    for (let y = 2; y < H - 2; y++) {
      for (let x = 2; x < W - 2; x++) {
        const r = resp[y * W + x]!;
        if (r < 0.02) continue;
        let isMax = true;
        for (let oy = -radius; oy <= radius && isMax; oy++) {
          for (let ox = -radius; ox <= radius; ox++) {
            if (resp[(y + oy) * W + (x + ox)]! > r) {
              isMax = false;
              break;
            }
          }
        }
        if (isMax) found.push({ x, y, r });
      }
    }
    found.sort((p, q) => q.r - p.r);
    this.points = found.slice(0, MAX_POINTS).map((p) => ({ x: p.x, y: p.y, age: 0, id: this.nextId++ }));
  }

  /** Local block-match a point from prev→curr; returns new pos or null if lost. */
  private trackPoint(px: number, py: number): { x: number; y: number } | null {
    const prev = this.prev!;
    let best = Infinity;
    let bx = px;
    let by = py;
    for (let oy = -SEARCH; oy <= SEARCH; oy++) {
      for (let ox = -SEARCH; ox <= SEARCH; ox++) {
        const nx = px + ox;
        const ny = py + oy;
        if (nx < 1 || ny < 1 || nx >= W - 1 || ny >= H - 1) continue;
        let sad = 0;
        for (let wy = -1; wy <= 1; wy++) {
          for (let wx = -1; wx <= 1; wx++) {
            sad += Math.abs(this.curr[(ny + wy) * W + (nx + wx)]! - prev[(py + wy) * W + (px + wx)]!);
          }
        }
        if (sad < best) {
          best = sad;
          bx = nx;
          by = ny;
        }
      }
    }
    return best < 1.2 ? { x: bx, y: by } : null;
  }

  /**
   * Track one frame.
   *
   * @param source a `FrameProvider` (draws its CURRENT frame), or an image that
   *   already holds the exact frame to track. AlvaARBackend passes its own
   *   processing canvas — the pixels AlvaAR has just been given — because
   *   drawing the live `<video>` again 10-170 ms after AlvaAR ran samples a
   *   LATER frame than the one the pose belongs to, and triangulation then pairs
   *   pose N with 2D points from frame N+k. (In replay the provider does not
   *   advance mid-step, so both paths draw the same frame there.)
   */
  update(source: FrameProvider | CanvasImageSource): TrackResult {
    const empty: TrackResult = { dx: 0, dy: 0, confidence: 0, points: [] };
    if (!this.grab(source)) return empty;

    if (!this.prev) {
      this.prev = this.curr.slice();
      this.detect();
      return { dx: 0, dy: 0, confidence: 0, points: this.exportPoints() };
    }

    // Track existing points; collect displacements.
    const dxs: number[] = [];
    const dys: number[] = [];
    const next: Pt[] = [];
    for (const p of this.points) {
      const np = this.trackPoint(Math.round(p.x), Math.round(p.y));
      if (np) {
        dxs.push(np.x - p.x);
        dys.push(np.y - p.y);
        next.push({ x: np.x, y: np.y, age: p.age + 1, id: p.id });
      }
    }
    this.points = next;

    // Robust global translation = median displacement.
    const median = (arr: number[]) => {
      if (arr.length === 0) return 0;
      const s = [...arr].sort((a, b) => a - b);
      return s[Math.floor(s.length / 2)]!;
    };
    const mdx = median(dxs);
    const mdy = median(dys);
    const confidence = Math.max(0, Math.min(1, this.points.length / MAX_POINTS));

    this.prev.set(this.curr);
    // Re-detect when we've lost too many.
    if (this.points.length < MIN_POINTS) this.detect();

    return { dx: mdx / W, dy: mdy / H, confidence, points: this.exportPoints() };
  }

  private exportPoints(): TrackedPoint[] {
    return this.points.map((p) => ({ x: p.x / W, y: p.y / H, age: p.age, id: p.id }));
  }
}
